'use client'

/**
 * InfoPopover — inline contextual help popover.
 *
 * Click-triggered (not hover tooltip). Uses Radix-backed @base-ui Popover from
 * src/components/ui/popover.tsx. No next/dynamic wrapper is needed here because
 * @base-ui Popover does NOT generate non-deterministic SSR IDs (it uses React's
 * useId under the hood which is stable across server + client). Consumers that
 * include InfoPopover inside a server-rendered layout should verify — if you
 * encounter hydration warnings, wrap the consumer with next/dynamic ssr:false,
 * not this component.
 *
 * WHEN: beside a card title, column header, or metric label to explain a concept
 *   without cluttering the layout.
 * NEVER: for primary navigation, destructive confirmations, or more than one per
 *   visible card header.
 *
 * @example
 * // Minimal (title only)
 * <InfoPopover title="How improvements work">
 *   Ranked recommendations regenerated after each scan.
 * </InfoPopover>
 *
 * // With rich children
 * <InfoPopover title="Share of Voice" side="bottom" align="start">
 *   <p>Percentage of brand mentions across all tracked AI answers.</p>
 * </InfoPopover>
 *
 * // No title (children carry all content)
 * <InfoPopover>Why does this matter? AI engines cite trusted sources…</InfoPopover>
 *
 * The trigger renders as `<Button variant="unstyled" size="auto">`
 * (ButtonPrimitive-migration, 2026-08-30). Confirmed against the Claude
 * Design source's own `InfoPopover.jsx`: its trigger chip is a bespoke
 * `rs-info__chip` element too, not modeled as a variant of their `Button` —
 * the atom supplies only native button semantics here, same as
 * `ButtonPrimitive` did.
 */

import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InfoPopoverProps {
  /**
   * Optional title rendered as text-body font-medium at the top of the popover.
   * Omit when children are self-explanatory.
   */
  title?: string
  /** Popover body content. May be string or rich ReactNode. */
  children: ReactNode
  /**
   * Screen-reader label for the trigger button.
   * Defaults to "More info" when not provided.
   */
  triggerLabel?: string
  /** Popover open side. Defaults to 'bottom'. */
  side?: 'bottom' | 'top' | 'left' | 'right'
  /** Popover alignment relative to trigger. Defaults to 'center'. */
  align?: 'center' | 'start' | 'end'
  /**
   * Visible text after the icon, inside the trigger (e.g. "How we count this"). Omitted, the
   * trigger is the icon alone, as at every other call site.
   */
  label?: string
  /** Extra className applied to the popover content panel. */
  className?: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InfoPopover({
  title,
  children,
  triggerLabel = 'More info',
  side = 'bottom',
  align = 'center',
  label,
  className,
}: InfoPopoverProps): React.ReactElement {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="unstyled"
            size="auto"
            className={cn(
              'text-muted-foreground duration-[var(--duration-base)] hover:text-foreground',
              label ? 'inline-flex items-center gap-1' : 'translate-y-px'
            )}
            aria-label={label ? undefined : triggerLabel}
          />
        }
      >
        <Info className="size-3.5" aria-hidden />
        {label}
      </PopoverTrigger>

      <PopoverContent side={side} align={align} className={cn('w-72', className)}>
        {title ? <p className="text-body font-medium text-foreground">{title}</p> : null}
        <div className={cn('text-caption text-muted-foreground leading-relaxed', title && 'mt-1')}>
          {children}
        </div>
      </PopoverContent>
    </Popover>
  )
}
